import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";

const NODE_STYLE = {
  aggregate_parent: { radius: 26, fill: "#312e81", stroke: "#1e1b4b", label: "#1e1b4b" },
  aggregate: { radius: 13, fill: "#6366f1", stroke: "#4338ca", label: "#3730a3" },
  aggregate_subtheme: { radius: 17, fill: "#a5b4fc", stroke: "#6366f1", label: "#4338ca" },
  claim: { radius: 5, fill: "#94a3b8", stroke: "#64748b", label: "#475569" },
  entity: { radius: 9, fill: "#f59e0b", stroke: "#b45309", label: "#92400e" },
  domain: { radius: 6, fill: "#64748b", stroke: "#475569", label: "#475569" },
};

const ENTITY_TYPE_FILL = {
  company: "#f59e0b",
  person: "#10b981",
  organization: "#f97316",
};

const SECTION_PALETTE = [
  "#0ea5e9", "#8b5cf6", "#ec4899", "#14b8a6", "#f43f5e",
  "#84cc16", "#a855f7", "#06b6d4", "#eab308", "#3b82f6",
];

const LINK_STYLE = {
  claim_entity: { stroke: "#f59e0b", opacity: 0.35, width: 1, strength: 0.2, distance: 55 },
  entity_relation: { stroke: "#fb923c", opacity: 0.3, width: 1, strength: 0.05, distance: 120 },
  claim_domain: { stroke: "#94a3b8", opacity: 0.4, width: 1, strength: 0.12, distance: 50 },
  claim_aggregate: { stroke: "#818cf8", opacity: 0.45, width: 1.1, strength: 0.55, distance: 45 },
  claim_parent: { stroke: "#7c3aed", opacity: 0.6, width: 1.5, strength: 0.35, distance: 70, dash: "6,4" },
  hierarchy_node: { stroke: "#6366f1", opacity: 0.55, width: 1.4, strength: 0.75, distance: 80 },
  aggregate_hierarchy: { stroke: "#4f46e5", opacity: 0.6, width: 1.6, strength: 0.8, distance: 85 },
};

function sectionColor(sectionKey, sectionIndex) {
  if (!sectionKey) return NODE_STYLE.claim.fill;
  const index = sectionIndex.get(sectionKey);
  return SECTION_PALETTE[(index ?? 0) % SECTION_PALETTE.length];
}

function nodeRadius(node) {
  if (node.kind === "entity") {
    return Math.min(18, NODE_STYLE.entity.radius + Math.sqrt(Math.max(0, node.degree - 1)) * 1.8);
  }
  if (node.kind === "aggregate") {
    return Math.min(20, NODE_STYLE.aggregate.radius + Math.sqrt(Math.max(0, node.claimCount ?? 0)) * 0.9);
  }
  return NODE_STYLE[node.kind]?.radius ?? 5;
}

function nodeFill(node, sectionIndex) {
  if (node.kind === "claim") return sectionColor(node.primarySection, sectionIndex);
  if (node.kind === "entity") return ENTITY_TYPE_FILL[String(node.entityType ?? "").toLowerCase()] ?? NODE_STYLE.entity.fill;
  return NODE_STYLE[node.kind]?.fill ?? "#94a3b8";
}

function truncate(value, max = 80) {
  const text = String(value ?? "");
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export default function ResearchKnowledgeGraph({
  graph = null,
  claims = [],
  entities = [],
  onOpenDeepDive = null,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const simulationRef = useRef(null);
  const [showDomains, setShowDomains] = useState(false);
  const [showUnlinkedClaims, setShowUnlinkedClaims] = useState(true);
  const [minEntityDegree, setMinEntityDegree] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  const claimById = useMemo(() => {
    const map = new Map();
    (claims ?? []).forEach((claim) => {
      if (claim?.id) map.set(claim.id, claim);
    });
    return map;
  }, [claims]);

  const entityById = useMemo(() => {
    const map = new Map();
    (entities ?? []).forEach((entity) => {
      if (entity?.id) map.set(entity.id, entity);
    });
    return map;
  }, [entities]);

  const { nodes, links, sectionIndex, maxEntityDegree } = useMemo(() => {
    const rawNodes = Array.isArray(graph?.nodes) ? graph.nodes : [];
    const rawEdges = Array.isArray(graph?.edges) ? graph.edges : [];

    const degree = new Map();
    rawEdges.forEach((edge) => {
      degree.set(edge.source, (degree.get(edge.source) ?? 0) + 1);
      degree.set(edge.target, (degree.get(edge.target) ?? 0) + 1);
    });

    const sectionKeys = [...new Set(
      rawNodes.filter((node) => node.kind === "claim" && node.primarySection).map((node) => node.primarySection),
    )].sort();
    const sections = new Map(sectionKeys.map((key, index) => [key, index]));

    let candidateNodes = rawNodes.map((node) => ({
      ...node,
      degree: degree.get(node.id) ?? 0,
    }));

    if (!showDomains) {
      candidateNodes = candidateNodes.filter((node) => node.kind !== "domain");
    }
    const entityDegrees = candidateNodes.filter((n) => n.kind === "entity").map((n) => n.degree);
    const maxDegree = entityDegrees.length ? Math.max(...entityDegrees) : 1;
    if (minEntityDegree > 1) {
      candidateNodes = candidateNodes.filter((node) => node.kind !== "entity" || node.degree >= minEntityDegree);
    }
    if (!showUnlinkedClaims) {
      const linkedIds = new Set();
      const kept = new Set(candidateNodes.map((n) => n.id));
      rawEdges.forEach((edge) => {
        if (edge.kind === "claim_entity" && kept.has(edge.source) && kept.has(edge.target)) {
          linkedIds.add(edge.source);
        }
      });
      candidateNodes = candidateNodes.filter((node) => node.kind !== "claim" || linkedIds.has(node.id));
    }

    const nodeIds = new Set(candidateNodes.map((node) => node.id));
    const visibleLinks = rawEdges
      .filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target))
      .map((edge) => ({ ...edge }));

    return { nodes: candidateNodes, links: visibleLinks, sectionIndex: sections, maxEntityDegree: maxDegree };
  }, [graph, showDomains, showUnlinkedClaims, minEntityDegree]);

  const adjacency = useMemo(() => {
    const map = new Map();
    links.forEach((link) => {
      const source = typeof link.source === "object" ? link.source.id : link.source;
      const target = typeof link.target === "object" ? link.target.id : link.target;
      if (!map.has(source)) map.set(source, new Set());
      if (!map.has(target)) map.set(target, new Set());
      map.get(source).add(target);
      map.get(target).add(source);
    });
    return map;
  }, [links]);

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId],
  );

  useEffect(() => {
    if (selectedNodeId && !selectedNode) {
      setSelectedNodeId(null);
    }
  }, [selectedNodeId, selectedNode]);

  const searchMatches = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return null;
    return new Set(
      nodes
        .filter((node) => String(node.label ?? "").toLowerCase().includes(term))
        .map((node) => node.id),
    );
  }, [nodes, searchTerm]);

  useEffect(() => {
    const container = containerRef.current;
    const svgEl = svgRef.current;
    if (!container || !svgEl) return undefined;

    const width = container.clientWidth || 900;
    const height = container.clientHeight || 620;

    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();
    svg.attr("viewBox", [0, 0, width, height]);

    const root = svg.append("g");

    const zoom = d3.zoom()
      .scaleExtent([0.15, 5])
      .on("zoom", (event) => root.attr("transform", event.transform));
    svg.call(zoom);

    const simNodes = nodes.map((node) => ({ ...node }));
    const simLinks = links.map((link) => ({ ...link }));

    const simulation = d3.forceSimulation(simNodes)
      .force("link", d3.forceLink(simLinks)
        .id((d) => d.id)
        .strength((d) => LINK_STYLE[d.kind]?.strength ?? 0.2)
        .distance((d) => LINK_STYLE[d.kind]?.distance ?? 60))
      .force("charge", d3.forceManyBody().strength((d) =>
        d.kind === "aggregate_parent" ? -420 : d.kind === "aggregate_subtheme" ? -280 : d.kind === "aggregate" ? -220 : d.kind === "entity" ? -140 : -40))
      .force("collide", d3.forceCollide().radius((d) => nodeRadius(d) + 3))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("x", d3.forceX(width / 2).strength(0.03))
      .force("y", d3.forceY(height / 2).strength(0.03));
    simulationRef.current = simulation;

    const link = root.append("g")
      .selectAll("line")
      .data(simLinks)
      .join("line")
      .attr("class", "kg-link")
      .attr("stroke", (d) => LINK_STYLE[d.kind]?.stroke ?? "#cbd5e1")
      .attr("stroke-opacity", (d) => LINK_STYLE[d.kind]?.opacity ?? 0.3)
      .attr("stroke-width", (d) => LINK_STYLE[d.kind]?.width ?? 1)
      .attr("stroke-dasharray", (d) => LINK_STYLE[d.kind]?.dash ?? null);

    const node = root.append("g")
      .selectAll("g")
      .data(simNodes)
      .join("g")
      .attr("class", "kg-node")
      .attr("cursor", "pointer")
      .call(d3.drag()
        .on("start", (event, d) => {
          if (!event.active) simulation.alphaTarget(0.25).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on("drag", (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on("end", (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));

    node.each(function drawNode(d) {
      const group = d3.select(this);
      const radius = nodeRadius(d);
      if (d.kind === "entity") {
        group.append("rect")
          .attr("x", -radius)
          .attr("y", -radius)
          .attr("width", radius * 2)
          .attr("height", radius * 2)
          .attr("rx", 3)
          .attr("transform", "rotate(45)")
          .attr("fill", nodeFill(d, sectionIndex))
          .attr("stroke", NODE_STYLE.entity.stroke)
          .attr("stroke-width", 1.2);
      } else {
        group.append("circle")
          .attr("r", radius)
          .attr("fill", nodeFill(d, sectionIndex))
          .attr("stroke", NODE_STYLE[d.kind]?.stroke ?? "#64748b")
          .attr("stroke-width", d.kind === "aggregate_parent" ? 2 : 1.2);
      }
      if (d.kind === "aggregate_parent" || d.kind === "aggregate" || d.kind === "aggregate_subtheme" || d.kind === "entity" || d.kind === "domain") {
        group.append("text")
          .attr("dy", radius + 11)
          .attr("text-anchor", "middle")
          .attr("font-size", d.kind === "aggregate_parent" ? 12 : d.kind === "domain" ? 9 : 10)
          .attr("font-weight", d.kind === "aggregate_parent" ? 700 : 500)
          .attr("fill", NODE_STYLE[d.kind]?.label ?? "#475569")
          .attr("paint-order", "stroke")
          .attr("stroke", "#ffffff")
          .attr("stroke-width", 3)
          .text(truncate(d.label, d.kind === "aggregate_parent" ? 42 : 28));
      }
      group.append("title").text(
        d.kind === "claim"
          ? String(claimById.get(d.id)?.text ?? d.label ?? d.id)
          : String(d.label ?? d.id),
      );
    });

    node.on("click", (event, d) => {
      event.stopPropagation();
      setSelectedNodeId((previous) => (previous === d.id ? null : d.id));
    });
    svg.on("click", () => setSelectedNodeId(null));

    simulation.on("tick", () => {
      link
        .attr("x1", (d) => d.source.x)
        .attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x)
        .attr("y2", (d) => d.target.y);
      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    return () => {
      simulation.stop();
      simulationRef.current = null;
    };
  }, [nodes, links, sectionIndex, claimById]);

  // Selection / search emphasis without rebuilding the simulation.
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const svg = d3.select(svgEl);
    const neighborIds = selectedNodeId ? adjacency.get(selectedNodeId) ?? new Set() : null;

    svg.selectAll("g.kg-node").attr("opacity", (d) => {
      if (!d) return 1;
      if (searchMatches && !searchMatches.has(d.id)) return 0.12;
      if (!selectedNodeId) return 1;
      if (d.id === selectedNodeId || neighborIds?.has(d.id)) return 1;
      return 0.15;
    });
    svg.selectAll("line.kg-link").attr("opacity", (d) => {
      if (!d) return 1;
      const source = typeof d.source === "object" ? d.source.id : d.source;
      const target = typeof d.target === "object" ? d.target.id : d.target;
      if (searchMatches && !(searchMatches.has(source) || searchMatches.has(target))) return 0.05;
      if (!selectedNodeId) return null;
      return source === selectedNodeId || target === selectedNodeId ? 1 : 0.06;
    });
  }, [selectedNodeId, adjacency, searchMatches]);

  const handleDeepDive = useCallback(() => {
    if (!selectedNode || typeof onOpenDeepDive !== "function") return;
    if (selectedNode.kind === "entity") {
      onOpenDeepDive({ anchorType: "entity", anchorIds: [selectedNode.id], label: selectedNode.label });
    } else if (selectedNode.kind === "claim") {
      onOpenDeepDive({ anchorType: "claim", anchorIds: [selectedNode.id], label: truncate(selectedNode.label, 60) });
    } else if (selectedNode.kind === "aggregate") {
      onOpenDeepDive({ anchorType: "aggregate", anchorIds: [selectedNode.id], label: selectedNode.label });
    } else if (selectedNode.kind === "aggregate_parent") {
      const childAggregateIds = links
        .filter((link) => link.kind === "aggregate_hierarchy" &&
          (typeof link.target === "object" ? link.target.id : link.target) === selectedNode.id)
        .map((link) => (typeof link.source === "object" ? link.source.id : link.source));
      onOpenDeepDive({ anchorType: "aggregate", anchorIds: childAggregateIds, label: selectedNode.label });
    }
  }, [selectedNode, onOpenDeepDive, links]);

  const selectedDetail = useMemo(() => {
    if (!selectedNode) return null;
    const neighborIds = adjacency.get(selectedNode.id) ?? new Set();
    const neighborNodes = nodes.filter((node) => neighborIds.has(node.id));
    const neighborEntities = neighborNodes.filter((node) => node.kind === "entity");
    const neighborClaims = neighborNodes.filter((node) => node.kind === "claim");
    const neighborAggregates = neighborNodes.filter((node) => node.kind === "aggregate" || node.kind === "aggregate_parent" || node.kind === "aggregate_subtheme");
    const neighborDomains = neighborNodes.filter((node) => node.kind === "domain");
    const fullClaim = selectedNode.kind === "claim" ? claimById.get(selectedNode.id) : null;
    const fullEntity = selectedNode.kind === "entity" ? entityById.get(selectedNode.id) : null;
    return { neighborEntities, neighborClaims, neighborAggregates, neighborDomains, fullClaim, fullEntity };
  }, [selectedNode, adjacency, nodes, claimById, entityById]);

  const counts = useMemo(() => ({
    themes: nodes.filter((n) => n.kind === "aggregate_parent").length,
    topics: nodes.filter((n) => n.kind === "aggregate").length,
    claims: nodes.filter((n) => n.kind === "claim").length,
    entities: nodes.filter((n) => n.kind === "entity").length,
  }), [nodes]);

  if (!graph || !Array.isArray(graph.nodes) || graph.nodes.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-slate-500">
        Graph data is not available yet.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-200 px-3 py-2 text-xs text-slate-600">
        <input
          type="search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Find entity or topic…"
          className="w-48 rounded border border-slate-300 px-2 py-1 text-xs focus:border-indigo-400 focus:outline-none"
        />
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={showUnlinkedClaims}
            onChange={(event) => setShowUnlinkedClaims(event.target.checked)}
          />
          Claims without entities
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={showDomains}
            onChange={(event) => setShowDomains(event.target.checked)}
          />
          Source domains
        </label>
        <label className="flex items-center gap-1.5">
          Entity min claims
          <input
            type="range"
            min={1}
            max={Math.max(2, maxEntityDegree)}
            value={minEntityDegree}
            onChange={(event) => setMinEntityDegree(Number(event.target.value))}
            className="w-24"
          />
          <span className="w-4 tabular-nums">{minEntityDegree}</span>
        </label>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full" style={{ background: NODE_STYLE.aggregate_parent.fill }} /> theme ({counts.themes})</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: NODE_STYLE.aggregate.fill }} /> topic ({counts.topics})</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-slate-400" /> claim ({counts.claims})</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rotate-45" style={{ background: NODE_STYLE.entity.fill }} /> entity ({counts.entities})</span>
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        <div ref={containerRef} className="relative min-h-0 flex-1">
          <svg ref={svgRef} className="h-full w-full" />
        </div>
        {selectedNode && selectedDetail ? (
          <div className="flex w-80 shrink-0 flex-col overflow-y-auto border-l border-slate-200 bg-slate-50 p-3 text-xs">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {selectedNode.kind === "aggregate_parent" ? "Theme" : selectedNode.kind === "aggregate" ? "Topic" : selectedNode.kind}
            </div>
            <div className="mb-2 text-sm font-semibold text-slate-800">
              {selectedNode.kind === "claim"
                ? (selectedDetail.fullClaim?.text ?? selectedNode.label)
                : selectedNode.label}
            </div>
            {selectedNode.kind === "claim" && selectedDetail.fullClaim ? (
              <div className="mb-2 space-y-1 text-slate-500">
                {selectedDetail.fullClaim.primarySection ? <div>Section: {selectedDetail.fullClaim.primarySection}</div> : null}
                {selectedDetail.fullClaim.date ? <div>Date: {selectedDetail.fullClaim.date}</div> : null}
                {Number.isFinite(Number(selectedDetail.fullClaim.relevance)) ? (
                  <div>Relevance {Number(selectedDetail.fullClaim.relevance).toFixed(2)} · Confidence {Number(selectedDetail.fullClaim.confidence ?? 0).toFixed(2)}</div>
                ) : null}
              </div>
            ) : null}
            {selectedNode.kind === "entity" && selectedDetail.fullEntity ? (
              <div className="mb-2 space-y-1 text-slate-500">
                <div>Type: {selectedDetail.fullEntity.type ?? "unknown"}</div>
                {selectedDetail.fullEntity.domain ? <div>Domain: {selectedDetail.fullEntity.domain}</div> : null}
              </div>
            ) : null}
            {selectedDetail.neighborAggregates.length ? (
              <div className="mb-2">
                <div className="mb-1 font-semibold text-slate-600">In hierarchy</div>
                {selectedDetail.neighborAggregates.map((node) => (
                  <button
                    key={node.id}
                    type="button"
                    onClick={() => setSelectedNodeId(node.id)}
                    className="block w-full truncate rounded px-1 py-0.5 text-left text-indigo-700 hover:bg-indigo-50"
                  >
                    {node.label}
                  </button>
                ))}
              </div>
            ) : null}
            {selectedDetail.neighborEntities.length ? (
              <div className="mb-2">
                <div className="mb-1 font-semibold text-slate-600">Entities ({selectedDetail.neighborEntities.length})</div>
                {selectedDetail.neighborEntities.slice(0, 12).map((node) => (
                  <button
                    key={node.id}
                    type="button"
                    onClick={() => setSelectedNodeId(node.id)}
                    className="block w-full truncate rounded px-1 py-0.5 text-left text-amber-700 hover:bg-amber-50"
                  >
                    {node.label}
                  </button>
                ))}
              </div>
            ) : null}
            {selectedDetail.neighborClaims.length ? (
              <div className="mb-2">
                <div className="mb-1 font-semibold text-slate-600">Claims ({selectedDetail.neighborClaims.length})</div>
                {selectedDetail.neighborClaims.slice(0, 15).map((node) => (
                  <button
                    key={node.id}
                    type="button"
                    onClick={() => setSelectedNodeId(node.id)}
                    className="block w-full rounded px-1 py-0.5 text-left text-slate-600 hover:bg-slate-100"
                  >
                    {truncate(claimById.get(node.id)?.text ?? node.label, 110)}
                  </button>
                ))}
              </div>
            ) : null}
            {selectedDetail.neighborDomains.length ? (
              <div className="mb-2">
                <div className="mb-1 font-semibold text-slate-600">Sources ({selectedDetail.neighborDomains.length})</div>
                {selectedDetail.neighborDomains.map((node) => (
                  <button
                    key={node.id}
                    type="button"
                    onClick={() => setSelectedNodeId(node.id)}
                    className="block w-full truncate rounded px-1 py-0.5 text-left text-slate-600 hover:bg-slate-100"
                  >
                    {node.label}
                  </button>
                ))}
              </div>
            ) : null}
            {selectedNode.kind === "claim" && !selectedDetail.neighborDomains.length && !showDomains ? (
              <div className="mb-2 text-[11px] text-slate-400">
                Enable “Source domains” above to see this claim’s sources in the graph.
              </div>
            ) : null}
            {typeof onOpenDeepDive === "function" ? (
              <button
                type="button"
                onClick={handleDeepDive}
                className="mt-auto rounded bg-indigo-600 px-2 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500"
              >
                Deep dive on this
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
